"use client";

import { SqarpLogo } from "@/components/pro-blocks/sqarp-logo";
import RichText from "@/components/RichText";
import Link from "next/link";
import { Separator } from "@/components/ui/separator";
import type { DefaultTypedEditorState } from "@payloadcms/richtext-lexical";

export type Footer7Props = {
	addressCompany?: string;
	addressOrgNo?: string;
	addressLine?: string;
	phone?: string;
	email?: string;
	footerColumns?: {
		title?: string;
		content?: DefaultTypedEditorState;
		id?: string | null;
	}[];
	legalLinks?: { label: string; href: string }[];
};

const toPhoneHref = (phone: string): string =>
	`tel:${phone.replace(/[^\d+]/g, "")}`;

export function Footer7({
	addressCompany,
	addressOrgNo,
	addressLine,
	phone,
	email,
	footerColumns,
	legalLinks,
}: Footer7Props) {
	const company = addressCompany?.trim() || undefined;
	const orgNo = addressOrgNo?.trim() || undefined;
	const line = addressLine?.trim() || undefined;
	const phoneDisplay = phone?.trim() || undefined;
	const emailDisplay = email?.trim() || undefined;

	const columns =
		footerColumns?.filter((column) => column?.title && column?.content) ?? [];
	const footerLegalLinks = legalLinks ?? [];
	const hasAddressDetails = Boolean(company || orgNo || line);
	const hasContactDetails = Boolean(phoneDisplay || emailDisplay);

	return (
		<footer
			id="contact-footer"
			className="container container-padding-x mx-auto max-md:py-10 pb-16 text-base"
			role="contentinfo"
			aria-label="Site footer"
		>
			<div className="flex flex-col gap-12">
				<div className="result-box" />
				<Separator role="presentation" />
				{/* Main Footer Grid */}
				<div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-5 lg:items-start lg:gap-6 text-left">
					<div className="flex w-full flex-col items-start gap-6 pr-0 lg:col-span-2 lg:items-start lg:pr-12">
						<Link href="/" aria-label="Go to homepage">
							<SqarpLogo className="h-[1.2rem] w-auto text-black md:h-5" />
						</Link>
						{hasAddressDetails ? (
							<div className="flex flex-col gap-1 text-left">
								<h3 className="text-foreground text-base font-medium">
									Address:
								</h3>
								{company ? (
									<p className="text-muted-foreground">{company}</p>
								) : null}
								{orgNo ? (
									<p className="text-muted-foreground">{orgNo}</p>
								) : null}
								{line ? <p className="text-muted-foreground">{line}</p> : null}
							</div>
						) : null}
						{hasContactDetails ? (
							<div className="flex flex-col gap-1 text-left">
								{phoneDisplay ? (
									<>
										<h3 className="text-foreground text-base font-medium">
											Contact:
										</h3>
										<a
											href={toPhoneHref(phoneDisplay)}
											className="text-muted-foreground hover:underline"
										>
											{phoneDisplay}
										</a>
									</>
								) : null}
								{emailDisplay ? (
									<>
										{phoneDisplay ? <br /> : null}
										<h3 className="text-foreground text-base font-medium">
											Email:
										</h3>
										<a
											href={`mailto:${emailDisplay}`}
											className="text-muted-foreground hover:underline"
										>
											{emailDisplay}
										</a>
									</>
								) : null}
							</div>
						) : null}
					</div>
					{columns.map((column, index) => (
						<div
							key={column.id ?? `footer-column-${index}`}
							className="flex flex-col gap-4"
						>
							<h3 className="text-foreground text-base font-medium">
								{column.title}
							</h3>
							<RichText
								data={column.content}
								enableGutter={false}
								enableProse={false}
								className="[&_p]:mb-3 [&_p]:text-muted-foreground [&_a]:text-muted-foreground [&_a]:no-underline hover:[&_a]:text-foreground"
							/>
						</div>
					))}
				</div>

				{/* Section Divider */}
				<Separator role="presentation" />

				{/* Bottom Section */}
				<div className="flex flex-col gap-12 text-left lg:flex-row lg:items-center lg:justify-between">
					{/* Copyright Text */}
					{company ? (
						<p className="text-muted-foreground order-2 text-left lg:order-1">
							© {new Date().getFullYear()} — {company}
						</p>
					) : null}
					{/* Legal Navigation */}
					{footerLegalLinks.length ? (
						<nav
							className="order-1 flex flex-col items-start gap-4 text-left lg:order-2 lg:flex-row lg:items-start lg:gap-8"
							aria-label="Legal links"
						>
							{footerLegalLinks.map((link) => (
								<Link
									key={link.label}
									href={link.href}
									className="text-muted-foreground hover:text-foreground text-left transition-colors"
								>
									{link.label}
								</Link>
							))}
						</nav>
					) : null}
				</div>
			</div>
		</footer>
	);
}
